export type NavigationIcon =
  | 'dashboard'
  | 'portfolio'
  | 'funds'
  | 'analysis'
  | 'copilot'
  | 'reports'
  | 'knowledge'
  | 'explore'
  | 'more';

/**
 * Surfaces removed from navigation while G2RS compliance mode is enabled
 * (AI products, rankings and investment-ideas pages). Consumers resolve the
 * `NON_G2RS_MODE` feature flag (database-backed) and pass the boolean here.
 */
const G2RS_MODE_BLOCKED_HREFS = new Set([
  '/chat',
  '/copiloto',
  '/ferramentas#relatorios',
  '/ferramentas/relatorios-ai',
  '/ideias-para-investir-por-objetivo',
  '/landing/copiloto',
  '/landing/mcp',
  '/landing/relatorios-ai',
  '/rankings-de-fiis',
  '/relatorios-ia',
]);

/** True when the link targets a surface hidden by G2RS mode. */
export function isG2rsModeBlockedHref(href: string, g2rsMode: boolean): boolean {
  return g2rsMode && G2RS_MODE_BLOCKED_HREFS.has(href);
}

export function filterG2rsModeLinks(links: ObjectiveNavLink[], g2rsMode: boolean): ObjectiveNavLink[] {
  return g2rsMode ? links.filter((link) => !G2RS_MODE_BLOCKED_HREFS.has(link.url)) : links;
}

export function filterG2rsModeSections(sections: ObjectiveNavSection[], g2rsMode: boolean): ObjectiveNavSection[] {
  if (!g2rsMode) return sections;
  return sections
    .map((section) => ({ ...section, links: filterG2rsModeLinks(section.links, g2rsMode) }))
    .filter((section) => section.links.length > 0);
}

export function filterG2rsModeGroups(groups: ObjectiveNavGroup[], g2rsMode: boolean): ObjectiveNavGroup[] {
  if (!g2rsMode) return groups;
  return groups
    .map((group) => ({ ...group, links: filterG2rsModeLinks(group.links, g2rsMode) }))
    .filter((group) => group.links.length > 0);
}

/**
 * While G2RS mode is on, the "Análise" entry (anchor on the tools hub)
 * becomes "Catálogo": the analysis tools it advertised (rankings, ideas)
 * are hidden and only the factual catalog surface remains relevant.
 */
export function resolveG2rsModeNavLink(link: ObjectiveNavLink, g2rsMode: boolean): ObjectiveNavLink {
  if (!g2rsMode || link.url !== '/ferramentas#analise') return link;
  return { ...link, title: 'Catálogo', description: 'Catálogo, comparador e indicadores para estudar FIIs.' };
}

export type ObjectiveNavLink = {
  title: string;
  compactTitle?: string;
  url: string;
  description?: string;
  featureFlag?: string;
  requiresAuth?: boolean;
  navigationIcon?: NavigationIcon;
  soon?: boolean;
};

export type ObjectiveNavSection = {
  title: string;
  links: ObjectiveNavLink[];
  navigationIcon?: NavigationIcon;
};

export type ObjectiveNavGroup = {
  title: string;
  navigationIcon: NavigationIcon;
  links: ObjectiveNavLink[];
};

export const funcionalidadesNavLink: ObjectiveNavLink = {
  title: 'Funcionalidades',
  url: '/landing/carteira',
  description: 'Conheça tudo que o Análise de FIIs organiza para você.',
};

export const homeNavLink: ObjectiveNavLink = {
  title: 'Início',
  url: '/',
};

export const exploreProductNavLink: ObjectiveNavLink = {
  title: 'Conheça o produto',
  compactTitle: 'Produto',
  url: '/',
  description: 'Veja como o Análise de FIIs organiza seus investimentos.',
};

export const blogNavLink: ObjectiveNavLink = {
  title: 'Blog',
  url: '/blog',
  description: 'Leia os conteúdos e atualizações publicados pela equipe.',
};

export const copilotMarketingNavLink: ObjectiveNavLink = {
  title: 'Copiloto IA',
  url: '/landing/copiloto',
  description: 'Converse com fundos, carteira, relatórios e mercado.',
  navigationIcon: 'copilot',
};

export const aiReportsMarketingNavLink: ObjectiveNavLink = {
  title: 'Relatórios IA',
  url: '/landing/relatorios-ai',
  description: 'Automatize briefings personalizados por e-mail.',
  navigationIcon: 'reports',
};

export const mcpMarketingNavLink: ObjectiveNavLink = {
  title: 'MCP e integrações',
  url: '/landing/mcp',
  description: 'Use os dados do Análise de FIIs no ChatGPT e no Claude.',
};

export const quickReferenceNavLink: ObjectiveNavLink = {
  title: 'Referência rápida',
  url: '/referencia-rapida',
  description: 'Consulte conceitos e indicadores essenciais de FIIs.',
};

export const pricingNavLink: ObjectiveNavLink = {
  title: 'Planos',
  url: '/precos',
  description: 'Conheça a carteira gratuita, créditos e o plano Pro.',
};

export const toolsNavLink: ObjectiveNavLink = {
  title: 'Central de ferramentas',
  compactTitle: 'Ferramentas',
  url: '/ferramentas',
  description: 'Escolha a ferramenta certa para investigar seus fundos.',
};

const publicCatalogNavLink: ObjectiveNavLink = {
  title: 'Catálogo de FIIs',
  url: '/catalogo',
  description: 'Lista completa de FIIs com indicadores, rendimentos e histórico.',
};

const publicComparatorNavLink: ObjectiveNavLink = {
  title: 'Comparador de FIIs',
  url: '/comparador',
  description: 'Compare múltiplos ativos e compartilhe a análise por URL.',
};

export const marketNavLink: ObjectiveNavLink = {
  title: 'Mercado',
  url: '/mercado',
  description: 'Panorama de Selic, tendências e indicadores de mercado.',
};

export const fixedIncomeNavLink: ObjectiveNavLink = {
  title: 'Tesouro',
  url: '/renda-fixa',
  description: 'Compare títulos do Tesouro Direto, taxas, prazos e marcação a mercado.',
};

export const supportNavLink: ObjectiveNavLink = {
  title: 'Suporte',
  url: '/contato',
  description: 'Fale com a equipe e encontre ajuda para usar a plataforma.',
};

const courseNavLink: ObjectiveNavLink = {
  title: 'Cursos',
  url: '/landing/curso',
  description: 'Uma trilha para quem está começando a estudar FIIs.',
  soon: true,
};

const authenticatedCourseNavLink: ObjectiveNavLink = {
  ...courseNavLink,
  url: '/cursos',
  description: 'Estude por trilhas e acompanhe seu progresso.',
  requiresAuth: true,
  soon: undefined,
};

const productLandingNavSection: ObjectiveNavSection = {
  title: 'Conheça o produto',
  navigationIcon: 'explore',
  links: [
    {
      title: 'A proposta do Análise de FIIs',
      url: '/',
      description: 'Veja como o Análise de FIIs ajuda nas suas decisões.',
    },
    {
      title: 'Gerenciador de carteiras',
      url: '/landing/carteira',
      description: 'Organize patrimônio, renda, dividendos e desempenho.',
    },
    {
      title: 'Copiloto',
      url: '/landing/copiloto',
      description: 'Investigue fundos, carteira e mercado com a IA.',
    },
    {
      title: 'Curso de FIIs',
      url: '/landing/curso',
      description: 'Aprenda a estudar FIIs com uma trilha estruturada.',
    },
    {
      title: 'Relatórios com IA',
      url: '/landing/relatorios-ai',
      description: 'Receba análises personalizadas no ritmo que escolher.',
    },
    { ...mcpMarketingNavLink },
    {
      title: 'Renda fixa',
      url: '/landing/renda-fixa',
      description: 'Compare alternativas e entenda cenários de investimento.',
    },
  ],
};

const investigateNavSection: ObjectiveNavSection = {
  title: 'Investigar',
  navigationIcon: 'analysis',
  links: [
    { ...publicCatalogNavLink },
    {
      title: 'Rankings de FIIs',
      url: '/rankings-de-fiis',
      description: 'Ordene fundos por DY, P/VP, liquidez e outros indicadores.',
    },
    { ...publicComparatorNavLink, title: 'Comparador' },
    {
      title: 'Ideias por objetivo',
      url: '/ideias-para-investir-por-objetivo',
      description: 'Comece uma pesquisa a partir de um objetivo de investimento.',
    },
    { ...marketNavLink },
  ],
};

/** Public screens exposed to the Copiloto's screen-linking capability. */
export const publicNavLinks = [
  {
    ...blogNavLink,
  },
  {
    title: 'Planos',
    url: '/precos',
  },
  {
    ...copilotMarketingNavLink,
  },
  {
    ...aiReportsMarketingNavLink,
  },
  {
    ...mcpMarketingNavLink,
  },
  {
    ...quickReferenceNavLink,
  },
  {
    ...fixedIncomeNavLink,
  },
  {
    ...funcionalidadesNavLink,
  },
  {
    ...publicCatalogNavLink,
  },
  {
    ...publicComparatorNavLink,
  },
  { ...marketNavLink },
  { ...supportNavLink },
];

export const dashboardNavLink: ObjectiveNavLink = {
  title: 'Painel',
  url: '/painel',
  description: 'Visão geral rápida da sua área logada.',
  requiresAuth: true,
  navigationIcon: 'dashboard',
};

const publicDashboardNavLink: ObjectiveNavLink = {
  ...dashboardNavLink,
  url: '/landing/carteira',
  requiresAuth: undefined,
};

const publicCopilotNavLink: ObjectiveNavLink = {
  ...copilotMarketingNavLink,
  title: 'Copiloto',
};

export const carteiraNavLink: ObjectiveNavLink = {
  title: 'Minha carteira',
  url: '/carteira',
  description: 'Suas carteiras, dividendos, patrimônio e configurações.',
  requiresAuth: true,
  navigationIcon: 'portfolio',
};

export const chatNavLink: ObjectiveNavLink = {
  title: 'Copiloto',
  url: '/chat',
  description: 'Explore os dados da plataforma conversando com o Copiloto.',
  requiresAuth: true,
  navigationIcon: 'copilot',
};

export const fundsNavLink: ObjectiveNavLink = {
  title: 'Fundos',
  url: '/catalogo',
  description: 'Catálogo, rankings e ideias para estudar FIIs.',
  navigationIcon: 'funds',
};

export const analysisNavLink: ObjectiveNavLink = {
  title: 'Análise',
  url: '/ferramentas#analise',
  description: 'Catálogo, rankings, comparador e ideias para estudar FIIs.',
  navigationIcon: 'analysis',
};

export const reportsNavLink: ObjectiveNavLink = {
  title: 'Relatórios',
  url: '/ferramentas#relatorios',
  description: 'Crie briefings de IA e acompanhe análises recorrentes.',
  navigationIcon: 'reports',
};

const buildProductNavGroups = ({
  dashboardLink,
  copilotLink,
  courseLink,
}: {
  dashboardLink: ObjectiveNavLink;
  copilotLink: ObjectiveNavLink;
  courseLink: ObjectiveNavLink;
}): ObjectiveNavGroup[] => [
  {
    title: 'Explorar',
    navigationIcon: 'explore',
    links: [exploreProductNavLink, blogNavLink],
  },
  {
    title: 'Carteira',
    navigationIcon: 'portfolio',
    links: [dashboardLink, carteiraNavLink],
  },
  {
    title: 'Investimentos',
    navigationIcon: 'funds',
    links: [fundsNavLink, analysisNavLink, fixedIncomeNavLink, marketNavLink],
  },
  {
    title: 'Inteligência',
    navigationIcon: 'copilot',
    links: [copilotLink, toolsNavLink],
  },
  {
    title: 'Conhecimento',
    navigationIcon: 'knowledge',
    links: [courseLink, quickReferenceNavLink],
  },
];

/**
 * The public and authenticated headers share the same product hierarchy, but
 * marketing destinations adapt to the user's context: visitors see the
 * product landings, while authenticated users can open the product directly.
 */
export const authenticatedNavGroups: ObjectiveNavGroup[] = buildProductNavGroups({
  dashboardLink: dashboardNavLink,
  copilotLink: chatNavLink,
  courseLink: authenticatedCourseNavLink,
});
export const publicNavGroups: ObjectiveNavGroup[] = buildProductNavGroups({
  dashboardLink: publicDashboardNavLink,
  copilotLink: publicCopilotNavLink,
  courseLink: courseNavLink,
});

export const authenticatedPrimaryNavLinks: ObjectiveNavLink[] = authenticatedNavGroups.flatMap((group) => group.links);
export const publicPrimaryNavLinks: ObjectiveNavLink[] = publicNavGroups.flatMap((group) => group.links);

const productNavSections: ObjectiveNavSection[] = [
  investigateNavSection,
  productLandingNavSection,
  {
    title: 'Mais',
    navigationIcon: 'more',
    links: [pricingNavLink, supportNavLink],
  },
];

export const authenticatedNavSections: ObjectiveNavSection[] = productNavSections;
export const publicNavSections: ObjectiveNavSection[] = productNavSections;

/**
 * Kept as the compatibility export used by the AI screen catalog and other
 * discovery surfaces. It describes the public tool hierarchy.
 */
export const objectiveNavSections: ObjectiveNavSection[] = [...publicNavGroups, ...publicNavSections];

export const adminNavLinks = [
  {
    title: 'Dashboard',
    url: '/admin/dashboard',
  },
  {
    title: 'Campanhas',
    url: '/admin/campaigns',
  },
  {
    title: 'E-mails',
    url: '/admin/emails',
  },
  {
    title: 'Jobs diários',
    url: '/admin/dashboard/daily-jobs',
  },
  {
    title: 'Métricas de dados',
    url: '/admin/dashboard/data-metrics',
  },
  {
    title: 'Gestão de usuários',
    url: '/admin/dashboard/user-management',
  },
  {
    title: 'Carteiras',
    url: '/admin/dashboard/portfolios',
  },
  {
    title: 'Contatos',
    url: '/admin/dashboard/contacts',
  },
  {
    title: 'Feature Flags',
    url: '/admin/dashboard/feature-flags',
  },
  {
    title: 'Entregabilidade DMARC',
    url: '/admin/email-deliverability',
  },
];
